import { useMemo } from 'react';
import { Billing } from '@starjack/entities/billing';
import { LiftOperator } from '@/models/liftoperator';

const sortAlphabetically = (a: LiftOperator, b: LiftOperator) =>
    a.name.de.trim().localeCompare(b.name.de.trim());

const filterByName = (filterValue: string, liftOperator: LiftOperator) =>
    liftOperator.name.de
        .toLocaleLowerCase()
        .trim()
        .includes(filterValue.toLocaleLowerCase()) ||
    liftOperator.name.en
        ?.toLocaleLowerCase()
        ?.trim()
        ?.includes(filterValue.toLocaleLowerCase());

const splitLiftOperatorsBySales = (
    liftOperatorBilling: Billing['liftOperators'],
    liftOperators: LiftOperator[]
) => {
    const liftOperatorMap = liftOperators.reduce<Record<string, LiftOperator>>(
        (acc, liftOperator) => ({
            ...acc,
            [liftOperator.id]: liftOperator,
        }),
        {}
    );
    return liftOperatorBilling.reduce(
        (acc, lo) => {
            const liftOperator = liftOperatorMap[lo.liftOperatorId];

            if (!liftOperator) return acc;

            const hasSales =
                lo.billedKeycardsSum.amount > 0 ||
                lo.billedShopProductsSum.amount > 0 ||
                lo.producedTicketsSum.amount > 0 ||
                lo.internalProductionsCreditTotal.amount > 0 ||
                lo.billedKeycards > 0;

            if (hasSales) {
                acc[0].push(liftOperatorMap[lo.liftOperatorId]);
            } else {
                acc[1].push(liftOperatorMap[lo.liftOperatorId]);
            }

            return acc;
        },
        [[], []] as [LiftOperator[], LiftOperator[]]
    );
};

export const useSplitLiftOperatorsBySales = (
    liftOperators: LiftOperator[],
    billing: Billing,
    filterValue: string,
    isFetching: boolean
): [LiftOperator[], LiftOperator[]] => {
    return useMemo(() => {
        if (isFetching) return [[], []];

        const [withData, withoutData] = splitLiftOperatorsBySales(
            billing.liftOperators,
            liftOperators
        );

        const filterTrimmed = filterValue.trim();
        const splitValuesSorted: [LiftOperator[], LiftOperator[]] = [
            withData.sort(sortAlphabetically),
            withoutData.sort(sortAlphabetically),
        ];

        if (!filterTrimmed) {
            return splitValuesSorted;
        }

        return [
            splitValuesSorted[0].filter((lo) =>
                filterByName(filterTrimmed, lo)
            ),
            splitValuesSorted[1].filter((lo) =>
                filterByName(filterTrimmed, lo)
            ),
        ];
    }, [liftOperators, billing, filterValue, isFetching]);
};
